// dq-v2 F2.1: period-grouping helpers shared between TimetablesTab and the
// data-quality teacher-context modals (extracted unchanged from
// TimetablesTab.tsx so both views group identically).

export interface PeriodLike {
  validFrom: string | null
  validTo: string | null
}

export interface GroupableEntry {
  valid_from?: string | null
  valid_to?: string | null
}

export interface TimetablePeriod<E> {
  validFrom: string | null
  validTo: string | null
  entries: E[]
}

export function isPeriodCurrent(validFrom: string | null, validTo: string | null): boolean {
  const today = new Date()
  today.setHours(0, 0, 0, 0)

  if (validFrom) {
    const from = new Date(validFrom)
    if (today < from) return false
  }
  if (validTo) {
    const to = new Date(validTo)
    if (today > to) return false
  }
  return true
}

export function formatPeriodLabel(validFrom: string | null, validTo: string | null): string {
  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('de-CH', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    })
  }

  if (!validFrom && !validTo) return 'Unbegrenzt gültig'
  if (!validFrom) return `Bis ${formatDate(validTo!)}`
  if (!validTo) return `Ab ${formatDate(validFrom)}`
  return `${formatDate(validFrom)} – ${formatDate(validTo)}`
}

export function groupEntriesByPeriod<E extends GroupableEntry>(
  entries: E[]
): TimetablePeriod<E>[] {
  const periodMap = new Map<string, TimetablePeriod<E>>()
  for (const entry of entries) {
    const validFrom = entry.valid_from || null
    const validTo = entry.valid_to || null
    const periodKey = `${validFrom || 'null'}_${validTo || 'null'}`
    if (!periodMap.has(periodKey)) {
      periodMap.set(periodKey, { validFrom, validTo, entries: [] })
    }
    periodMap.get(periodKey)!.entries.push(entry)
  }
  const periods = Array.from(periodMap.values())
  periods.sort((a, b) => {
    const aIsCurrent = isPeriodCurrent(a.validFrom, a.validTo)
    const bIsCurrent = isPeriodCurrent(b.validFrom, b.validTo)
    if (aIsCurrent && !bIsCurrent) return -1
    if (!aIsCurrent && bIsCurrent) return 1
    const aFrom = a.validFrom || '0000-00-00'
    const bFrom = b.validFrom || '0000-00-00'
    return aFrom.localeCompare(bFrom)
  })
  return periods
}
